import React, { useState, useEffect } from 'react';
import { useSelector } from "react-redux";
import validarpermiso from '../../lib/utils';
import { Table, Space, Typography, Input, Button, message, Pagination } from 'antd';
import { SearchOutlined, DownloadOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { readItems } from "@directus/sdk";
import { getDirectusClient } from '../../lib/directus';
import Layouts from '../../components/Layouts';

const { Title } = Typography;

const Chips = () => {
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [hasPermissionError, setHasPermissionError] = useState(false);
  const permisos = useSelector((state) => state.permisos);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 25,
  });

  const fetchData = async (page = 1, pageSize = 25, search = '') => {
    if (!validarpermiso("ships_sin_uso", "read", permisos)) {
      setHasPermissionError(true);
      return;
    }

    setLoading(true);
    try {
      const directus = await getDirectusClient();
      
      // Obtener el recuento total para la paginación
      const countResponse = await directus.request(
        readItems('ships_sin_uso', {
          aggregate: {
            count: 'id'
          },
          filter: search ? {
            serial: {
              _contains: search
            }
          } : {}
        })
      );


    
      const totalItems = countResponse[0]?.count?.id;
      
      // Obtener los registros para la página actual
      const response = await directus.request(
        readItems('ships_sin_uso', {
          fields: ['id', 'serial', 'cliente.id', 'cliente.nombre'],
          filter: search ? {
            serial: {
              _contains: search
            }
          } : {},
          limit: pageSize,
          offset: (page - 1) * pageSize, // Usar offset en lugar de page para asegurar la paginación correcta
          sort: ['cliente.nombre']
        })
      );

      // Transform data to flat structure
      const flatData = response.map(item => ({
        id: item.id,
        serial: item.serial,
        clienteId: item.cliente?.id,
        clienteNombre: item.cliente?.nombre || 'Sin Cliente'
      }));
      
      setClients(flatData);
      setPagination({
        current: page,
        pageSize: pageSize,
        total: totalItems,
      });
      setHasPermissionError(false);

    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 403 || error.message?.includes('permission')) {
        setHasPermissionError(true);
        message.error('No tiene permisos para ver los chips');
      } else {
        message.error('Error al cargar los datos');
      }
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  // Asegurarse de que fetchData se ejecute cuando cambia la página actual
  useEffect(() => {
    fetchData(pagination.current, pagination.pageSize, searchText);
  }, [pagination.current, pagination.pageSize, searchText]);

  // Manejar el cambio de página de manera explícita


  const handleSearch = (value) => {
    setSearchText(value);
    // Resetear a la primera página cuando se realiza una búsqueda
    setPagination({
      ...pagination,
      current: 1,
    });
  };

  const handleExportExcel = async () => {
    if (!validarpermiso("ships_sin_uso", "read", permisos)) {
      message.error('No tiene permisos para exportar los datos');
      return;
    }

    try {
      setLoading(true);
      const directus = await getDirectusClient();
      
      // Fetch all data without pagination
      const allData = await directus.request(
        readItems('ships_sin_uso', {
          fields: ['serial', 'cliente.nombre'],
          sort: ['cliente.nombre'],
          limit: -1 // This ensures we get all records
        })
      );

      // Transform data for Excel
      const excelData = allData.map(item => ({
        'Cliente': item.cliente?.nombre || 'Sin Cliente',
        'Serial': item.serial
      }));

      // Create workbook and worksheet with custom column widths
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      // Set column widths
      ws['!cols'] = [
        { wch: 30 }, // Cliente
        { wch: 15 }  // Serial
      ];

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Seriales');

      // Generate Excel file with timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      XLSX.writeFile(wb, `seriales_${timestamp}.xlsx`);
      
      message.success(`Se exportaron ${allData.length} registros exitosamente`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      message.error('Error al exportar a Excel');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Cliente',
      dataIndex: 'clienteNombre',
      key: 'clienteNombre',
    },
    {
      title: 'Serial',
      dataIndex: 'serial',
      key: 'serial',
    }
  ];
console.log(pagination);

  return (
    <Layouts>
      {!hasPermissionError ? (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Title level={4}>Chips</Title>
            <Button 
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleExportExcel}
              loading={loading}
              disabled={!validarpermiso("ships_sin_uso", "read", permisos)}
            >
              Exportar a Excel
            </Button>
          </Space>

          <Input
            placeholder="Buscar por serial"
            prefix={<SearchOutlined />}
            onChange={(e) => handleSearch(e.target.value)}
            style={{ maxWidth: 300 }}
            allowClear
            disabled={!validarpermiso("ships_sin_uso", "read", permisos)}
          />

          <Table
            loading={loading}
            dataSource={clients}
            columns={columns}
            rowKey="id"
            pagination={false} // Disable table pagination
          />

          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            showSizeChanger={true}
            showQuickJumper={true}
            showTotal={(total) => `Total: ${total} registros`}
            pageSizeOptions={['10', '25', '50', '100']}
            onChange={(page, pageSize) => {
              setPagination({
                ...pagination,
                current: page,
                pageSize: pageSize,
              });
            }}
            style={{ marginTop: 16, textAlign: 'right' }}
            disabled={!validarpermiso("ships_sin_uso", "read", permisos)}
          />
        </Space>
      ) : (
        <h3>No tiene permisos para ver los chips</h3>
      )}
    </Layouts>
  );
};

export default Chips;